import { useRef, useState } from "react";
import { Link } from "react-router-dom";
import { websiteHost } from "scripts/application/setting";
import { useUserContext } from "services/UserContext";
import { buildUserMenuItems } from "utils/userMenu";

interface IMainHeaderProps {
    children: React.ReactNode;
    /**
     * Closes the mobile navigation drawer. The drawer itself is this
     * component's `#MainNavBar` collapse, revealed by the `menu_opened` body
     * class that DefaultLayout toggles from the mobile hamburger.
     */
    onCloseMenu?: () => void;
    /** Logs the user out from the drawer's account menu. */
    onSignOut?: () => void;
}

const getHref = (href: string): string => {
    if (href.startsWith("/")) {
        return `${websiteHost}${href}`;
    }
    return href;
};

export const MainHeader = (props: IMainHeaderProps): JSX.Element => {
    const [email, setEmail] = useState("");
    const emailInputRef = useRef<HTMLInputElement | null>(null);
    const { user } = useUserContext();

    const handleSignOut = (e: React.MouseEvent) => {
        e.preventDefault();
        props.onSignOut?.();
    };

    const handleSubscribe = (e?: React.FormEvent | React.MouseEvent) => {
        if (e) e.preventDefault();

        const trimmed = email.trim();
        if (trimmed.length === 0) {
            window.alert("Enter email address.");
            emailInputRef.current?.focus();
            return;
        }

        const subscribeUrl = getHref(
            `/insight/preferences/?email=${encodeURIComponent(trimmed)}`
        );
        // window.location.href = subscribeUrl;
        window.open(subscribeUrl, "_blank", "noopener,noreferrer");
    };

    // Close the drawer when a real navigation link is tapped, but leave the
    // section toggles (Buy/Rent, Home Values, …) alone so Bootstrap can expand
    // them in place.
    const handleNavClick = (e: React.MouseEvent<HTMLElement>) => {
        if (!props.onCloseMenu) return;
        const target = e.target;
        if (!(target instanceof Element)) return;
        const anchor = target.closest("a");
        if (anchor && !anchor.classList.contains("dropdown-toggle")) {
            props.onCloseMenu();
        }
    };

    return (
        <nav className="navbar navbar-expand-md har-nav navbar_mini">
            <div
                className="container container--v2"
                style={{ justifyContent: "flex-end" }}
            >
                <a
                    href={websiteHost}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="navbar-brand"
                >
                    <img
                        alt="HAR"
                        src="https://content.harstatic.com/resources/images/common/logo-blue.svg"
                    />
                </a>
                <a
                    className="resp_nav_close"
                    href="#"
                    onClick={(e) => {
                        e.preventDefault();
                        props.onCloseMenu?.();
                    }}
                >
                    <img
                        loading="lazy"
                        style={{ width: 14 }}
                        alt="close"
                        src="https://content.harstatic.com/media/icons/close.svg"
                    />
                </a>
                <div
                    className="collapse navbar-collapse"
                    id="MainNavBar"
                    onClick={handleNavClick}
                >
                    <a
                        href={websiteHost}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="resp_nav_logo"
                    >
                        <img
                            style={{ width: 56 }}
                            alt="HAR"
                            src="https://content.harstatic.com/resources/images/common/logo-blue.svg"
                        />
                    </a>
                    <Link to={"/"} className="d-block mb-3 mx-3">
                        <img src="https://content.harstatic.com/media/icons/a_all/ai_mode_beta.svg" style={{ maxWidth: 100 }} alt="AI Mode" />
                    </Link>
                    <ul className="navbar-nav mr-auto me-auto">
                        <li className="nav-item dropdown ">
                            <a
                                className="nav-link dropdown-toggle"
                                href="#"
                                role="button"
                                data-toggle="dropdown"
                                data-bs-toggle="dropdown"
                                aria-haspopup="true"
                                aria-expanded="false"
                                target="_blank"
                                rel="noopener noreferrer"
                            >
                                Buy/Rent
                                <img
                                    loading="lazy"
                                    alt="dropdown image"
                                    className="linkarrow"
                                    src="https://content.harstatic.com/resources/images/icons/red_down_arrow.svg"
                                />
                            </a>
                            <div
                                className="dropdown-menu dropdown-buyrent"
                                aria-labelledby="navbarDropdown"
                            >
                                <ul className="dropdown-content font_size--medium font_weight--semi_bold">
                                    <li>
                                        <ul className="mn_section">
                                            <li className="font_size--large font_weight--bold color_black pb-3">
                                                Buy
                                            </li>
                                            <li>
                                                <a
                                                    href={getHref("/search/single_family_homes")}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                >
                                                    Single Family Homes
                                                </a>
                                            </li>
                                            <li>
                                                <a
                                                    href={getHref("/search/townhouse_condo")}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                >
                                                    Townhouses/Condos
                                                </a>
                                            </li>
                                            <li>
                                                <a
                                                    href={getHref("/search/multi_family")}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                >
                                                    Multi-Family Homes
                                                </a>
                                            </li>
                                            <li>
                                                <a
                                                    href={getHref("/search/residential_lots_and_land")}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                >
                                                    Land and Lots
                                                </a>
                                            </li>
                                            <li>
                                                <a
                                                    href={getHref("/search/country_homes_acreage")}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                >
                                                    Country Homes/Acreage
                                                </a>
                                            </li>
                                            <li>
                                                <a
                                                    href={getHref("/search/mid_high_rise_condominium")}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                >
                                                    Mid/High-Rise Condos{" "}
                                                </a>
                                            </li>
                                            <li>
                                                <a
                                                    href={getHref("/mapsearch")}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                >
                                                    Search by Map
                                                </a>
                                            </li>
                                        </ul>
                                        <ul className="mn_section">
                                            <li className="font_size--large font_weight--bold color_black pb-3">
                                                Rent
                                            </li>
                                            <li>
                                                <a
                                                    href={getHref("/rentals")}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                >
                                                    Rental Search
                                                </a>
                                            </li>
                                            <li>
                                                <a
                                                    href={getHref(
                                                        "/search/single_family_homes?for_sale=0"
                                                    )}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                >
                                                    Single Family Homes
                                                </a>
                                            </li>
                                            <li>
                                                <a
                                                    href={getHref("/search/townhouse_condo?for_sale=0")}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                >
                                                    Townhouses/Condos
                                                </a>
                                            </li>
                                            <li>
                                                <a
                                                    href={getHref("/search/multi_family?for_sale=0")}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                >
                                                    Multi-Family Homes
                                                </a>
                                            </li>
                                            <li>
                                                <a
                                                    href={getHref(
                                                        "/search/mid_high_rise_condominium?for_sale=0"
                                                    )}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                >
                                                    Mid/High-Rise Condos{" "}
                                                </a>
                                            </li>
                                            <li>
                                                <a
                                                    href={getHref("/apartments")}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                >
                                                    Apartments
                                                </a>
                                            </li>
                                            <li>
                                                <a
                                                    href={getHref("/mobile/#rental")}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                >
                                                    Rentals App
                                                </a>
                                            </li>
                                            <li>
                                                <a
                                                    href={getHref(
                                                        "/mortgage/time-value-calculators?CALCULATORID=HF05"
                                                    )}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                >
                                                    Buying vs Renting Calculator
                                                </a>
                                            </li>
                                            <li>
                                                <a
                                                    href={getHref("/mapsearch?for_sale=0")}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                >
                                                    Search by Map
                                                </a>
                                            </li>
                                        </ul>
                                        <ul className="mn_section">
                                            <li className="font_size--large font_weight--bold color_black pb-3">
                                                Specialties
                                            </li>
                                            <li>
                                                <a
                                                    href={getHref("/drivetimesearch/")}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                >
                                                    Drive Time{" "}
                                                    <img
                                                        width="15px"
                                                        src="https://content.harstatic.com/media/icons/DriveTime.svg"
                                                        alt="car icon"
                                                    />
                                                </a>
                                            </li>
                                            <li>
                                                <a
                                                    href={getHref("/new-homes-by-city")}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                >
                                                    Just Listed
                                                </a>
                                            </li>
                                            <li>
                                                <a
                                                    href={getHref("/openhouse")}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                >
                                                    Open Houses
                                                </a>
                                            </li>
                                            <li>
                                                <a
                                                    href={getHref("/new-construction-by-city")}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                >
                                                    New Homes
                                                </a>
                                            </li>
                                            <li>
                                                <a
                                                    href={getHref("/luxury-homes-for-sale-by-city")}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                >
                                                    Luxury Homes
                                                </a>
                                            </li>
                                            <li>
                                                <a
                                                    href={getHref("/lots-and-land-for-sale-by-city")}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                >
                                                    Land and Lots
                                                </a>
                                            </li>
                                            <li>
                                                <a
                                                    href={getHref("/price-reduced-homes-by-city")}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                >
                                                    Price Reduction
                                                </a>
                                            </li>
                                            <li>
                                                <a
                                                    href={getHref("/foreclosed-homes-by-city")}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                >
                                                    Foreclosure
                                                </a>
                                            </li>
                                            <li>
                                                <a
                                                    href="https://www.commgate.com/"
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                >
                                                    Commercial Properties
                                                </a>
                                            </li>
                                            <li>
                                                <a
                                                    href={getHref("/international")}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                >
                                                    Global Properties (International)
                                                </a>
                                            </li>
                                        </ul>
                                        <div className="graphics_section font_weight--regular">
                                            <div className="p-4">
                                                <h3 tabIndex={0} className="mb-2">
                                                    Get the Top Real Estate App
                                                </h3>
                                                <div className="pb-3 d-flex">
                                                    <div className="mr-3 me-3">
                                                        <img
                                                            loading="lazy"
                                                            alt="Full Star"
                                                            className="pb-1"
                                                            src="https://content.harstatic.com/media/icons/stars/star_full.svg"
                                                        />
                                                        <img
                                                            loading="lazy"
                                                            alt="Full Star"
                                                            className="pb-1"
                                                            src="https://content.harstatic.com/media/icons/stars/star_full.svg"
                                                        />
                                                        <img
                                                            loading="lazy"
                                                            alt="Full Star"
                                                            className="pb-1"
                                                            src="https://content.harstatic.com/media/icons/stars/star_full.svg"
                                                        />
                                                        <img
                                                            loading="lazy"
                                                            alt="Full Star"
                                                            className="pb-1"
                                                            src="https://content.harstatic.com/media/icons/stars/star_full.svg"
                                                        />
                                                        <img
                                                            loading="lazy"
                                                            alt="Half and quarter star"
                                                            className="pb-1"
                                                            src="https://content.harstatic.com/media/icons/stars/star_half_and_quarter.svg"
                                                        />
                                                    </div>
                                                    <div className="color_cement_dark">
                                                        4.8 • 130K Ratings
                                                    </div>
                                                </div>
                                                <div className="pb-2">
                                                    <a
                                                        rel="nofollow noopener noreferrer"
                                                        className="d-inline-block pr-3 pe-3 pb-3"
                                                        href="https://itunes.apple.com/us/app/har-com-houston-real-estate/id386981161"
                                                        target="_blank"
                                                    >
                                                        <img
                                                            loading="lazy"
                                                            style={{ maxWidth: 123 }}
                                                            src="https://content.harstatic.com/media/icons/download-on-the-app-store-black.svg"
                                                            alt="Download on the App Store"
                                                        />
                                                    </a>
                                                    <a
                                                        rel="nofollow noopener noreferrer"
                                                        className="d-inline-block pb-3"
                                                        href="https://play.google.com/store/apps/details?id=com.har.androidapp&hl=en"
                                                        target="_blank"
                                                    >
                                                        <img
                                                            loading="lazy"
                                                            style={{ maxWidth: 135 }}
                                                            src="https://content.harstatic.com/media/icons/google-play-badge_black.svg"
                                                            alt="Get it on Google Play"
                                                        />
                                                    </a>
                                                </div>
                                                <a
                                                    href={getHref("/mobile/")}
                                                    className="font_weight--semi_bold font_size--medium"
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                >
                                                    View other HAR apps -&gt;
                                                </a>
                                            </div>
                                        </div>
                                    </li>
                                </ul>
                            </div>
                        </li>
                        <li className="nav-item dropdown cols_3 ">
                            <a
                                className="nav-link dropdown-toggle"
                                href="#"
                                role="button"
                                data-toggle="dropdown"
                                data-bs-toggle="dropdown"
                                aria-haspopup="true"
                                aria-expanded="false"
                                target="_blank"
                                rel="noopener noreferrer"
                            >
                                Home Values
                                <img
                                    loading="lazy"
                                    alt="dropdown image"
                                    className="linkarrow"
                                    src="https://content.harstatic.com/resources/images/icons/red_down_arrow.svg"
                                />
                            </a>
                            <div className="dropdown-menu" aria-labelledby="navbarDropdown">
                                <ul className="dropdown-content  font_size--medium font_weight--semi_bold">
                                    <li>
                                        <ul className="mn_section">
                                            <li className="font_size--large font_weight--bold color_black pb-3">
                                                Home Values
                                            </li>
                                            <li>
                                                <a
                                                    href={getHref("/homevalues")}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                >
                                                    Home Values Search
                                                </a>
                                            </li>
                                            <li>
                                                <a
                                                    href={getHref("/trackhomevalue")}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                >
                                                    Track Home Value
                                                </a>
                                            </li>
                                            <li>
                                                <a
                                                    href={getHref("/homevalues/compare")}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                >
                                                    Compare Home Values Instantly
                                                </a>
                                            </li>
                                            <li>
                                                <a
                                                    href={getHref("/homevalues/TexasAppraisalDistricts")}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                >
                                                    List of Appraisal Districts
                                                </a>
                                            </li>
                                            <li>
                                                <a
                                                    href={getHref("/insight_category/homevalue")}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                >
                                                    Home Value Resource
                                                </a>
                                            </li>
                                            <li>
                                                <a
                                                    href={getHref(
                                                        "/homeownersvideos/category/view/home-value/homevalue"
                                                    )}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                >
                                                    Videos About Home Values
                                                </a>
                                            </li>
                                        </ul>
                                        <ul className="mn_section">
                                            <li className="font_size--large font_weight--bold color_black pb-3">
                                                Home Selling Tips &amp; Tools
                                            </li>
                                            {/*<li><a href="/mortgage/calculators/buying-vs-renting-calculator/HF05">Buying vs Renting Calculator</a></li>*/}
                                            <li>
                                                <a
                                                    href={getHref("/insight_category/selling")}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                >
                                                    Home Selling Resource
                                                </a>
                                            </li>
                                            <li>
                                                <a
                                                    href={getHref("/answers_category/home-selling_14")}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                >
                                                    Selling Q&amp;A
                                                </a>
                                            </li>
                                            <li>
                                                <a
                                                    href={getHref("/blogs_category/home-selling_14")}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                >
                                                    Latest Blogs about Selling
                                                </a>
                                            </li>
                                        </ul>
                                        <div className="graphics_section">
                                            <div
                                                className="font_size--large_extra_extra font_weight--bold color_black pb-3"
                                                style={{ maxWidth: 250 }}
                                            >
                                                Compare the value of your house!
                                            </div>
                                            <div className="color_slate font-weight-normal fw-normal font_size--small pb-3">
                                                Try our new tool that lets you compare home values
                                                instantly from leading sources.
                                            </div>
                                            <a
                                                href={getHref("/homevalues/compare")}
                                                className="btn btn--prominent mb-5"
                                                target="_blank"
                                                rel="noopener noreferrer"
                                            >
                                                Get Home Value Comparables
                                            </a>
                                            <img
                                                loading="lazy"
                                                alt="Home Values"
                                                className="img-fluid"
                                                src="https://content.harstatic.com/media/temprary/menu_graphics_2.webp"
                                            />
                                        </div>
                                    </li>
                                </ul>
                            </div>
                        </li>
                        <li className="nav-item dropdown ">
                            <a
                                className="nav-link dropdown-toggle"
                                href="#"
                                role="button"
                                data-toggle="dropdown"
                                data-bs-toggle="dropdown"
                                aria-haspopup="true"
                                aria-expanded="false"
                                target="_blank"
                                rel="noopener noreferrer"
                            >
                                Explore
                                <img
                                    loading="lazy"
                                    alt="dropdown image"
                                    className="linkarrow"
                                    src="https://content.harstatic.com/resources/images/icons/red_down_arrow.svg"
                                />
                            </a>
                            <div className="dropdown-menu" aria-labelledby="navbarDropdown">
                                <ul className="dropdown-content  font_size--medium font_weight--semi_bold">
                                    <li>
                                        <ul className="mn_section">
                                            <li className="font_size--large font_weight--bold color_black pb-3">
                                                Schools
                                            </li>
                                            <li>
                                                <a
                                                    href={getHref("/school")}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                >
                                                    Public Schools
                                                </a>
                                            </li>
                                            <li>
                                                <a
                                                    href={getHref("/charter_schools")}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                >
                                                    Charter Schools
                                                </a>
                                            </li>
                                            <li>
                                                <a
                                                    href={getHref("/texas_private_schools")}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                >
                                                    Private Schools
                                                </a>
                                            </li>
                                            <li>
                                                <a
                                                    href={getHref("/school/search-compare")}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                >
                                                    Compare Schools
                                                </a>
                                            </li>
                                            <li>
                                                <a
                                                    href={getHref(
                                                        "/school/search/advance?grade=E&schtype=0"
                                                    )}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                >
                                                    Search Elementary Schools
                                                </a>
                                            </li>
                                            <li>
                                                <a
                                                    href={getHref(
                                                        "/school/search/advance?grade=M&schtype=0"
                                                    )}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                >
                                                    Search Middle Schools
                                                </a>
                                            </li>
                                            <li>
                                                <a
                                                    href={getHref(
                                                        "/school/search/advance?grade=S&schtype=0"
                                                    )}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                >
                                                    Search High Schools
                                                </a>
                                            </li>
                                            <li>
                                                <a
                                                    href={getHref("/best_elementary_schools")}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                >
                                                    Best Elementary Schools
                                                </a>
                                            </li>
                                            <li>
                                                <a
                                                    href={getHref("/best_middle_schools")}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                >
                                                    Best Middle Schools
                                                </a>
                                            </li>
                                            <li>
                                                <a
                                                    href={getHref("/best_high_schools")}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                >
                                                    Best High Schools
                                                </a>
                                            </li>
                                            <li>
                                                <a
                                                    href={getHref("/top_high_schools_by_SAT")}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                >
                                                    High Schools with Top SAT Scores
                                                </a>
                                            </li>
                                            <li>
                                                <a
                                                    href={getHref("/school/texas_school_districts")}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                >
                                                    School Districts
                                                </a>
                                            </li>
                                        </ul>
                                        <ul className="mn_section">
                                            <li className="font_size--large font_weight--bold color_black pb-3">
                                                Communities
                                            </li>
                                            <li>
                                                <a
                                                    href={getHref("/neighborhoods")}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                >
                                                    Neighborhoods
                                                </a>
                                            </li>
                                            <li>
                                                <a
                                                    href={getHref("/highrisefinder")}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                >
                                                    High-Rise Living
                                                </a>
                                            </li>
                                            <li>
                                                <a
                                                    href={getHref("/masterplanned")}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                >
                                                    Master-Planned Communities
                                                </a>
                                            </li>
                                            <li>
                                                <a
                                                    href={getHref("/golfcourses")}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                >
                                                    Golf Course Communities
                                                </a>
                                            </li>
                                            <li>
                                                <a
                                                    href={getHref("/seniorliving")}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                >
                                                    Senior Living
                                                </a>
                                            </li>
                                            <li>
                                                <a
                                                    href={getHref("/texasrealestate/?tab=mktarea")}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                >
                                                    Greater Houston Market Areas
                                                </a>
                                            </li>
                                            <li>
                                                <a
                                                    href={getHref("/historic_districts")}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                >
                                                    Historic Districts
                                                </a>
                                            </li>
                                            <li>
                                                <a
                                                    href={getHref("/texasrealestate?tab=city")}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                >
                                                    Texas Cities
                                                </a>
                                            </li>
                                            <li>
                                                <a
                                                    href={getHref("/texasrealestate?tab=county")}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                >
                                                    Texas Counties
                                                </a>
                                            </li>
                                            <li>
                                                <a
                                                    href={getHref("/texasrealestate?tab=zip")}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                >
                                                    Texas Zip Codes
                                                </a>
                                            </li>
                                            <li>
                                                <a
                                                    href={getHref("/homevalues/TexasAppraisalDistricts")}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                >
                                                    Appraisal Districts
                                                </a>
                                            </li>
                                            <li>
                                                <a
                                                    href={getHref("/texasrealestate/waterdistricts")}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                >
                                                    Water Districts
                                                </a>
                                            </li>
                                        </ul>
                                        <ul className="mn_section">
                                            <li className="font_size--large font_weight--bold color_black pb-3">
                                                Resources
                                            </li>
                                            <li>
                                                <a
                                                    href={getHref("/flood")}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                >
                                                    Flood Resource
                                                </a>
                                            </li>
                                            <li>
                                                <a
                                                    href={getHref("/answers")}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                >
                                                    Ask a Pro / Community
                                                </a>
                                            </li>
                                            <li>
                                                <a
                                                    href={getHref("/insight")}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                >
                                                    RealInsight (Newsletter){" "}
                                                </a>
                                            </li>
                                            <li>
                                                <a
                                                    href={getHref("/blogs")}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                >
                                                    Real Estate Blogs
                                                </a>
                                            </li>
                                            <li>
                                                <a
                                                    href={getHref(
                                                        "/insight_category/realestateinvestment"
                                                    )}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                >
                                                    Investors
                                                </a>
                                            </li>
                                            <li>
                                                <a
                                                    href={getHref("/content/consumer_knowledge")}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                >
                                                    Knowledge Videos
                                                </a>
                                            </li>
                                            <li>
                                                <a
                                                    href={getHref("/content/homeowners_videos")}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                >
                                                    Homeowner Videos
                                                </a>
                                            </li>
                                            <li>
                                                <a
                                                    href={getHref("/dictionary")}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                >
                                                    Knowledge Base
                                                </a>
                                            </li>
                                            <li>
                                                <a
                                                    href={getHref("/content/houston_neighborhood_videos")}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                >
                                                    Houston Neighborhood Videos
                                                </a>
                                            </li>
                                            <li>
                                                {/* HAR.com has no destination for this row yet, so it
                                                    renders as a plain label instead of a link. */}
                                                <a>Texas Electricity Learning</a>
                                            </li>
                                            <li>
                                                <a
                                                    href="https://cms.har.com/tools"
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                >
                                                    View All Tools
                                                </a>
                                            </li>
                                        </ul>
                                        <ul className="mn_section">
                                            <li className="font_size--large font_weight--bold color_black pb-3">
                                                Commercial
                                            </li>
                                            <li>
                                                <a
                                                    href="https://commgate.com/search/#/search/grid?bounds=29.905372412150157,-95.1553918031829,29.19815276895737,-96.3913537172454&locations=%7B%22geographies%22%3A%5B%7B%22regionCode%22%3A%22TX%22,%22country%22%3A%7B%22countryCode%22%3A%22USA%22%7D%7D%5D%7D&propertyCategories=RETAIL"
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                >
                                                    Retail
                                                </a>
                                            </li>
                                            <li>
                                                <a
                                                    href="https://commgate.com/search/#/search/grid?bounds=29.905372412150157,-95.1553918031829,29.19815276895737,-96.3913537172454&locations=%7B%22geographies%22%3A%5B%7B%22regionCode%22%3A%22TX%22,%22country%22%3A%7B%22countryCode%22%3A%22USA%22%7D%7D%5D%7D&propertyCategories=OFFICE"
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                >
                                                    Office
                                                </a>
                                            </li>
                                            <li>
                                                <a
                                                    href="https://commgate.com/search/#/search/grid?bounds=29.905372412150157,-95.1553918031829,29.19815276895737,-96.3913537172454&locations=%7B%22geographies%22%3A%5B%7B%22regionCode%22%3A%22TX%22,%22country%22%3A%7B%22countryCode%22%3A%22USA%22%7D%7D%5D%7D&propertyCategories=INDUSTRIAL"
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                >
                                                    Industrial
                                                </a>
                                            </li>
                                            <li>
                                                <a
                                                    href="https://commgate.com/search/#/search/grid?bounds=29.905372412150157,-95.1553918031829,29.19815276895737,-96.3913537172454&locations=%7B%22geographies%22%3A%5B%7B%22regionCode%22%3A%22TX%22,%22country%22%3A%7B%22countryCode%22%3A%22USA%22%7D%7D%5D%7D&propertyCategories=LAND"
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                >
                                                    Land
                                                </a>
                                            </li>
                                            <li>
                                                <a
                                                    href="https://commgate.com/search/#/search/grid?bounds=29.905372412150157,-95.1553918031829,29.19815276895737,-96.3913537172454&locations=%7B%22geographies%22%3A%5B%7B%22regionCode%22%3A%22TX%22,%22country%22%3A%7B%22countryCode%22%3A%22USA%22%7D%7D%5D%7D&propertyCategories=FARM_RANCH"
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                >
                                                    Farm/Ranch
                                                </a>
                                            </li>
                                            <li>
                                                <a
                                                    href="https://commgate.com/search/#/search/grid?bounds=29.905372412150157,-95.1553918031829,29.19815276895737,-96.3913537172454&locations=%7B%22geographies%22%3A%5B%7B%22regionCode%22%3A%22TX%22,%22country%22%3A%7B%22countryCode%22%3A%22USA%22%7D%7D%5D%7D&propertyCategories=HOSPITALITY"
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                >
                                                    Hospitality
                                                </a>
                                            </li>
                                            <li>
                                                <a
                                                    href="https://commgate.com/search/#/search/grid?bounds=29.905372412150157,-95.1553918031829,29.19815276895737,-96.3913537172454&locations=%7B%22geographies%22%3A%5B%7B%22regionCode%22%3A%22TX%22,%22country%22%3A%7B%22countryCode%22%3A%22USA%22%7D%7D%5D%7D&propertyCategories=SPECIALTY"
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                >
                                                    Specialty
                                                </a>
                                            </li>
                                            <li>
                                                <a
                                                    href="https://commgate.com/search/#/search/grid?bounds=29.905372412150157,-95.1553918031829,29.19815276895737,-96.3913537172454&locations=%7B%22geographies%22%3A%5B%7B%22regionCode%22%3A%22TX%22,%22country%22%3A%7B%22countryCode%22%3A%22USA%22%7D%7D%5D%7D&propertyCategories=MULTIFAMILY"
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                >
                                                    Multi-Family
                                                </a>
                                            </li>
                                        </ul>
                                    </li>
                                </ul>
                            </div>
                        </li>
                        <li className="nav-item dropdown ">
                            <a
                                className="nav-link dropdown-toggle"
                                href="#"
                                role="button"
                                data-toggle="dropdown"
                                data-bs-toggle="dropdown"
                                aria-haspopup="true"
                                aria-expanded="false"
                                target="_blank"
                                rel="noopener noreferrer"
                            >
                                Agents
                                <img
                                    loading="lazy"
                                    alt="dropdown image"
                                    className="linkarrow"
                                    src="https://content.harstatic.com/resources/images/icons/red_down_arrow.svg"
                                />
                            </a>
                            <div className="dropdown-menu" aria-labelledby="navbarDropdown">
                                <ul className="dropdown-content  font_size--medium font_weight--semi_bold">
                                    <li>
                                        <ul className="mn_section">
                                            <li className="font_size--large font_weight--bold color_black pb-3">
                                                Agents/Brokers
                                            </li>
                                            <li>
                                                <a
                                                    href={getHref("/realestatepro")}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                >
                                                    Real Estate Agents
                                                </a>
                                            </li>
                                            <li>
                                                <a
                                                    href={getHref(
                                                        "/realestatepro/search?search_type=member&term=&officecity=&officezip=&rating=1"
                                                    )}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                >
                                                    Agents with Ratings
                                                </a>
                                            </li>
                                            <li>
                                                <a
                                                    href={getHref("/content/platinum_agent")}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                >
                                                    Platinum Agents{" "}
                                                </a>
                                            </li>
                                            <li>
                                                <a
                                                    href={getHref("/realestatepro/multilingual")}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                >
                                                    Multi-lingual Agents
                                                </a>
                                            </li>
                                            <li>
                                                <a
                                                    href={getHref("/realestatepro/multicultural")}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                >
                                                    Multi-cultural Agents
                                                </a>
                                            </li>
                                            <li>
                                                <a
                                                    href={getHref("/realestatepro/designations")}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                >
                                                    Agents with Designations
                                                </a>
                                            </li>
                                            <li>
                                                <a
                                                    href={getHref("/realestatepro/brokers")}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                >
                                                    Real Estate Firms
                                                </a>
                                            </li>
                                            <li>
                                                <a
                                                    href={getHref("/trec")}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                >
                                                    Texas Real Estate Directory
                                                </a>
                                            </li>
                                            <li>
                                                <a
                                                    href={getHref("/realestatepro/listby/city")}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                >
                                                    Agents by City
                                                </a>
                                            </li>
                                            <li>
                                                <a
                                                    href={getHref("/realestatepro/listby/zipcode")}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                >
                                                    Agents by Zip Code
                                                </a>
                                            </li>
                                        </ul>
                                        <ul className="mn_section">
                                            <li className="font_size--large font_weight--bold color_black pb-3">
                                                Service Providers
                                            </li>
                                            <li>
                                                <a
                                                    href={getHref("/realestatepro/serviceproviders")}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                >
                                                    Local Service Providers
                                                </a>
                                            </li>
                                            <li>
                                                <a
                                                    href={getHref("/service_providers/inspectors/INSP")}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                >
                                                    Inspectors
                                                </a>
                                            </li>
                                            <li>
                                                <a
                                                    href={getHref(
                                                        "/service_providers/mortgage-services/MORT"
                                                    )}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                >
                                                    Mortgage Services
                                                </a>
                                            </li>
                                            <li>
                                                <a
                                                    href={getHref(
                                                        "/service_providers/insurance-_-general/INSGEN"
                                                    )}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                >
                                                    Insurance/ General
                                                </a>
                                            </li>
                                            <li>
                                                <a
                                                    href={getHref("/service_providers/pest-control/PEST")}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                >
                                                    Termite &amp; Pests
                                                </a>
                                            </li>
                                            <li>
                                                <a
                                                    href={getHref("/service_providers/staging/STAGE")}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                >
                                                    Home Staging
                                                </a>
                                            </li>
                                            <li>
                                                <a
                                                    href={getHref("/joinhar/signup?type=AF")}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                >
                                                    Service Provider Sign up
                                                </a>
                                            </li>
                                        </ul>
                                        <ul className="mn_section">
                                            <li className="font_size--large font_weight--bold color_black pb-3">
                                                For Pros
                                            </li>
                                            <li>
                                                <a
                                                    href={getHref("/mobile")}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                >
                                                    HAR.com Mobile App
                                                </a>
                                            </li>
                                            <li>
                                                <a
                                                    href={getHref("/content/realtor")}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                >
                                                    HAR Members Area
                                                </a>
                                            </li>
                                            <li>
                                                <a
                                                    href={getHref("/agentmax/signup?cmp=1")}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                >
                                                    Texas Realtors Claim Your Profile
                                                </a>
                                            </li>
                                            <li>
                                                <a
                                                    href={getHref("/education")}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                >
                                                    Find Real Estate Training{" "}
                                                </a>
                                            </li>
                                            <li>
                                                <a
                                                    href="https://cms.har.com/cer/"
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                >
                                                    Join Client Experience Rating
                                                </a>
                                            </li>
                                            <li>
                                                <a
                                                    href={getHref("/directions")}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                >
                                                    Route Planner / Directions
                                                </a>
                                            </li>
                                            <li>
                                                <a
                                                    href="https://cms.har.com/media/"
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                >
                                                    Media App
                                                </a>
                                            </li>
                                            <li>
                                                <a
                                                    href="https://cms.har.com/openhouse/"
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                >
                                                    Open House Registry App
                                                </a>
                                            </li>
                                            <li>
                                                <a
                                                    href={getHref("/mobile/#TexasAgents")}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                >
                                                    Texas Real Estate Directory App
                                                </a>
                                            </li>
                                            <li>
                                                <a
                                                    href={getHref("/trec")}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                >
                                                    Texas Real Estate Directory
                                                </a>
                                            </li>
                                            <li>
                                                <a
                                                    href={getHref("/joinhar")}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                >
                                                    Become a HAR Member
                                                </a>
                                            </li>
                                            <li>
                                                <a
                                                    href={getHref("/content/page/platinum_services")}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                >
                                                    Become a Platinum Agent
                                                </a>
                                            </li>
                                            <li>
                                                <a
                                                    href={getHref("/joinhar/signup?type=AF&trec_num=")}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                >
                                                    Become an Affiliate Member
                                                </a>
                                            </li>
                                            <li>
                                                <a
                                                    href="https://cms.har.com/realtor-pros-tools/"
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                >
                                                    Other Tools for Agents{" "}
                                                </a>
                                            </li>
                                        </ul>
                                        <div className="graphics_section">
                                            <div className="border-bottom pb-3 mb-3">
                                                <div
                                                    className="font_size--large_extra_extra font_weight--bold color_black pb-3"
                                                    style={{ maxWidth: 250 }}
                                                >
                                                    Ask a Pro
                                                </div>
                                                <div className="color_slate font-weight-normal fw-normal font_size--small pb-3">
                                                    Find real estate questions &amp; answers. Share
                                                    insights and experience. Get answers, ask questions
                                                    and more.
                                                </div>
                                                <a
                                                    href={getHref("/answers")}
                                                    className="btn btn--prominent mb-3"
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                >
                                                    Learn More
                                                </a>
                                            </div>
                                            <div className="pb-3 mb-3">
                                                <div className="font_size--large_extra_extra font_weight--bold color_black pb-3">
                                                    <span className="pr-lg-3 pr-md-2 pr-0 pe-lg-3 pe-md-2 pe-0">
                                                        Online Store
                                                    </span>{" "}
                                                    <img
                                                        loading="lazy"
                                                        alt="Online Store"
                                                        className="img-fluid mt-2"
                                                        src="https://content.harstatic.com/media/temprary/realtor_store_logo.png"
                                                    />
                                                </div>
                                                <a
                                                    href="https://store.har.com/"
                                                    className="btn btn--ordinary mb-3"
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                >
                                                    Learn More
                                                </a>
                                            </div>
                                        </div>
                                    </li>
                                </ul>
                            </div>
                        </li>
                        <li className="nav-item dropdown cols_3 ">
                            <a
                                className="nav-link dropdown-toggle"
                                href="#"
                                role="button"
                                data-toggle="dropdown"
                                data-bs-toggle="dropdown"
                                aria-haspopup="true"
                                aria-expanded="false"
                                target="_blank"
                                rel="noopener noreferrer"
                            >
                                Mortgage
                                <img
                                    loading="lazy"
                                    alt="dropdown image"
                                    className="linkarrow"
                                    src="https://content.harstatic.com/resources/images/icons/red_down_arrow.svg"
                                />
                            </a>
                            <div className="dropdown-menu" aria-labelledby="navbarDropdown">
                                <ul className="dropdown-content  font_size--medium font_weight--semi_bold">
                                    <li>
                                        <ul className="mn_section">
                                            <li className="font_size--large font_weight--bold color_black pb-3">
                                                Mortgage
                                            </li>
                                            <li>
                                                <a
                                                    href={getHref("/mortgage")}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                >
                                                    Mortgage Center
                                                </a>
                                            </li>
                                            <li>
                                                <a
                                                    href={getHref("/mortgage/rates-nearby")}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                >
                                                    Mortgage Rates Nearby
                                                </a>
                                            </li>
                                            <li>
                                                <a
                                                    href={getHref("/mortgage/calculator")}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                >
                                                    Mortgage Calculator
                                                </a>
                                            </li>
                                            <li>
                                                <a
                                                    href={getHref(
                                                        "/blogs_category/mortgage-and-finance_20"
                                                    )}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                >
                                                    Mortgage &amp; Finance Articles
                                                </a>
                                            </li>
                                            <li>
                                                <a
                                                    href={getHref("/mortgagedictionary")}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                >
                                                    Mortgage Dictionary
                                                </a>
                                            </li>
                                            <li>
                                                <a
                                                    href={getHref("/mortgage/types")}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                >
                                                    Mortgage Types
                                                </a>
                                            </li>
                                            <li>
                                                <a
                                                    href={getHref("/downpayment")}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                >
                                                    Down Payment Assistance Programs
                                                </a>
                                            </li>
                                            <li>
                                                <a
                                                    href={getHref(
                                                        "/homeownersvideos/category/view/financial/financial"
                                                    )}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                >
                                                    Financial Videos
                                                </a>
                                            </li>
                                        </ul>
                                        <ul className="mn_section">
                                            <li className="font_size--large font_weight--bold color_black pb-3">
                                                Home Financing
                                            </li>
                                            <li>
                                                <a
                                                    href={getHref(
                                                        "/mortgage/calculators/should-i-refinance/HF01"
                                                    )}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                >
                                                    Should I refinance?
                                                </a>
                                            </li>
                                            <li>
                                                <a
                                                    href={getHref(
                                                        "/mortgage/calculators/how-much-will-my-fixed-rate-mortgage-payment-be/HF02"
                                                    )}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                >
                                                    How much will my fixed rate mortgage payment be?
                                                </a>
                                            </li>
                                            <li>
                                                <a
                                                    href={getHref(
                                                        "/mortgage/calculators/how-much-will-my-adjustable-rate-mortgage-payments-be/HF03"
                                                    )}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                >
                                                    How much will my adjustable rate mortgage payments be?
                                                </a>
                                            </li>
                                            <li className="pb-md-4 pb-2">
                                                <a
                                                    href={getHref("/mortgage/financialcalculators")}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                >
                                                    See More →
                                                </a>
                                            </li>
                                            <li className="font_size--large font_weight--bold color_black pb-3">
                                                Personal Financing
                                            </li>
                                            <li>
                                                <a
                                                    href={getHref(
                                                        "/mortgage/calculators/how-much-car-can-i-afford/PC01"
                                                    )}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                >
                                                    How much car can I afford?
                                                </a>
                                            </li>
                                            <li>
                                                <a
                                                    href={getHref(
                                                        "/mortgage/calculators/how-long-will-it-take-to-pay-off-my-credit-card/PC02"
                                                    )}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                >
                                                    How long will it take to pay off my credit card?
                                                </a>
                                            </li>
                                            <li>
                                                <a
                                                    href={getHref(
                                                        "/mortgage/calculators/how-much-will-i-need-to-save-for-a-major-purchase/PC04"
                                                    )}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                >
                                                    How much will I need to save for a major purchase?
                                                </a>
                                            </li>
                                            <li className="pb-2">
                                                <a
                                                    href={getHref("/mortgage/financialcalculators")}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                >
                                                    See More →
                                                </a>
                                            </li>
                                        </ul>
                                        <div className="graphics_section">
                                            <div className="row no-gutters pb-4 g-0">
                                                <div className="col-auto">
                                                    <img
                                                        className="mr-3 me-3"
                                                        style={{ maxWidth: 60 }}
                                                        src="https://content.harstatic.com/media/artwork/payment_assistance.svg"
                                                        alt="Down Payment Assistance"
                                                    />
                                                </div>
                                                <div className="col font_size--large_extra_extra font_weight--bold color_black">
                                                    Down Payment Assistance
                                                </div>
                                            </div>
                                            <div className="color_slate font-weight-normal fw-normal font_size--medium pb-4">
                                                More than 2,000 down payment assistance programs to help
                                                reduce your costs of homeownership.
                                                <br />
                                                <br />
                                                Active Duty . Reserve/National Guard . Veteran .
                                                Educator . Law Enforcement . Firefighter . Healthcare
                                                providers and more
                                            </div>
                                            <a
                                                href={getHref("/downpayment")}
                                                className="btn btn--prominent btn--large mb-3"
                                                target="_blank"
                                                rel="noopener noreferrer"
                                            >
                                                Get Started
                                            </a>
                                        </div>
                                    </li>
                                </ul>
                            </div>
                        </li>
                        <li className="nav-item dropdown cols_3 ">
                            <a
                                className="nav-link dropdown-toggle"
                                href="#"
                                role="button"
                                data-toggle="dropdown"
                                data-bs-toggle="dropdown"
                                aria-haspopup="true"
                                aria-expanded="false"
                                target="_blank"
                                rel="noopener noreferrer"
                            >
                                More...
                            </a>
                            <div className="dropdown-menu" aria-labelledby="navbarDropdown">
                                <ul className="dropdown-content  font_size--medium font_weight--semi_bold">
                                    <li>
                                        <ul className="mn_section">
                                            <li className="font_size--large font_weight--bold color_black pb-3">
                                                RealInsight (Newsletter)
                                            </li>
                                            <li>
                                                <a
                                                    href={getHref("/insight_category/buying")}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                >
                                                    Home Buying
                                                </a>
                                            </li>
                                            <li>
                                                <a
                                                    href={getHref("/insight_category/selling")}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                >
                                                    Home Selling
                                                </a>
                                            </li>
                                            <li>
                                                <a
                                                    href={getHref(
                                                        "/insight_category/realestateinvestment"
                                                    )}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                >
                                                    Investing
                                                </a>
                                            </li>
                                            <li>
                                                <a
                                                    href={getHref("/insight_category/moving")}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                >
                                                    Moving
                                                </a>
                                            </li>
                                            <li>
                                                <a
                                                    href={getHref("/insight_category/repair")}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                >
                                                    Home Repair
                                                </a>
                                            </li>
                                            <li>
                                                <a
                                                    href={getHref("/insight_category/remodeling")}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                >
                                                    Remodeling
                                                </a>
                                            </li>
                                            <li>
                                                <a
                                                    href={getHref("/insight_category/appraisals")}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                >
                                                    Appraisals
                                                </a>
                                            </li>
                                            <li>
                                                <a
                                                    href={getHref("/insight_category/insurance")}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                >
                                                    Insurance
                                                </a>
                                            </li>
                                            <li>
                                                <a
                                                    href={getHref("/insight_category/taxes")}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                >
                                                    Taxes
                                                </a>
                                            </li>
                                            <li>
                                                <a
                                                    href={getHref("/insight_category/safety")}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                >
                                                    Safety
                                                </a>
                                            </li>
                                        </ul>
                                        <ul className="mn_section">
                                            <li className="font_size--large font_weight--bold color_black pb-3">
                                                About
                                            </li>
                                            <li>
                                                <a
                                                    href={getHref("/content/realtor")}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                >
                                                    HAR Member Area
                                                </a>
                                            </li>
                                            <li>
                                                <a
                                                    href="https://cms.har.com/about-us/"
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                >
                                                    About HAR
                                                </a>
                                            </li>
                                            <li>
                                                <a
                                                    href={getHref("/content/department/newsroom/")}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                >
                                                    Newsroom
                                                </a>
                                            </li>
                                            <li>
                                                <a
                                                    href={getHref("/content/webstats")}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                >
                                                    HAR.com/Mobile Statistics
                                                </a>
                                            </li>
                                            <li>
                                                <a
                                                    href="https://cms.har.com/code-of-ethics/"
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                >
                                                    Code of Ethics
                                                </a>
                                            </li>
                                        </ul>
                                        <ul className="mn_section">
                                            <li className="font_size--large font_weight--bold color_black pb-3">
                                                Contact
                                            </li>
                                            <li>
                                                <a
                                                    href="https://cms.har.com/help-center/"
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                >
                                                    Help Center
                                                </a>
                                            </li>
                                            <li>
                                                <a
                                                    href={getHref("/contact-us/")}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                >
                                                    Contact us
                                                </a>
                                            </li>
                                            <li>
                                                <a
                                                    href="https://cms.har.com/accessibility/"
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                >
                                                    Accessibility
                                                </a>
                                            </li>
                                            <li>
                                                <a
                                                    href="https://cms.har.com/privacy-policy-2/"
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                >
                                                    Privacy Policy
                                                </a>
                                            </li>
                                            <li>
                                                <a
                                                    href="https://cms.har.com/termsofuse/"
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                >
                                                    Terms of Use
                                                </a>
                                            </li>
                                            <li>
                                                <a
                                                    href={getHref("/content/sitesearch")}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                >
                                                    HAR Site Search
                                                </a>
                                            </li>
                                        </ul>
                                        <div className="graphics_section">
                                            <div className="row small-gutters pb-2">
                                                <div className="col-auto">
                                                    <a
                                                        href={getHref("/insight")}
                                                        target="_blank"
                                                        rel="noopener noreferrer"
                                                    >
                                                        <img
                                                            style={{ maxWidth: 70 }}
                                                            alt="Logo Real Insight"
                                                            src="https://content.harstatic.com/media/icons/realinsight/RealInsight_logo_3.svg"
                                                        />
                                                    </a>
                                                </div>
                                                <div className="col">
                                                    <div className="color_slate font-weight-normal fw-normal font_size--small pb-3">
                                                        Up-to-date real estate industry trends, news, and
                                                        insights.
                                                        <span className="font_weight--bold">
                                                            <a
                                                                href={getHref("/insight")}
                                                                target="_blank"
                                                                rel="noopener noreferrer"
                                                            >
                                                                {" "}
                                                                Go to RealInsight
                                                            </a>
                                                        </span>
                                                    </div>
                                                </div>
                                            </div>
                                            <div className="font_size--large_extra_extra font_weight--bold color_black pb-2">
                                                Sign up for the Newsletter
                                            </div>

                                            <form onSubmit={handleSubscribe}>
                                                <input
                                                    type="email"
                                                    id="realemail2"
                                                    className="form-control mb-2"
                                                    name="realemail"
                                                    aria-describedby="realemail"
                                                    placeholder="What's your email?"
                                                    ref={emailInputRef}
                                                    value={email}
                                                    onChange={(e) => setEmail(e.target.value)}
                                                />
                                                <button type="submit" className="btn btn--prominent mb-3">
                                                    Subscribe
                                                </button>
                                            </form>
                                            <div
                                                className="color_slate font_size--small_extra font_weight--regular pb-5"
                                                style={{ opacity: "0.75" }}
                                            >
                                                By subscribing, you accept our{" "}
                                                <span className="font_weight--bold">
                                                    <a
                                                        style={{ display: "inline-block" }}
                                                        href="https://cms.har.com/privacy-policy-2/"
                                                        target="_blank"
                                                        rel="noopener noreferrer"
                                                    >
                                                        privacy policy
                                                    </a>
                                                    .
                                                </span>
                                            </div>
                                        </div>
                                    </li>
                                </ul>
                            </div>
                        </li>
                        {/* data-matomo-mask: session recordings replay the DOM,
                            and this menu carries the visitor's name. */}
                        {!!user.user_id && (user.role_id ?? 0) !== 0 && (
                            <li
                                className="nav-item dropdown resp_user_menu d-md-none d-block"
                                data-matomo-mask
                            >
                                <a
                                    className="nav-link dropdown-toggle"
                                    href="#"
                                    role="button"
                                    data-toggle="dropdown"
                                    data-bs-toggle="dropdown"
                                    aria-haspopup="true"
                                    aria-expanded="false"
                                >
                                    My Account
                                </a>
                                <div className="dropdown-menu" aria-labelledby="navbarDropdown">
                                    <ul className="dropdown-content font_size--medium font-weight-bold fw-bold">
                                        <li>
                                            <ul className="mn_section">
                                                {buildUserMenuItems(user).map((item) => (
                                                    <li key={item.label}>
                                                        {item.isStatic ? (
                                                            <a className={item.className}>
                                                                {item.label}
                                                            </a>
                                                        ) : (
                                                            <a
                                                                className={item.className}
                                                                href={getHref(item.path)}
                                                                target="_blank"
                                                                rel="noopener noreferrer"
                                                            >
                                                                {item.label}
                                                                {item.isNew && (
                                                                    <>
                                                                        &nbsp;
                                                                        <span className="border_radius--default bg_color_pending pl-2 pr-2 ps-2 pe-2 pt-1 pb-1 color_snow_white font_weight--semi_bold text-center justify-content-center">
                                                                            NEW
                                                                        </span>
                                                                    </>
                                                                )}
                                                            </a>
                                                        )}
                                                    </li>
                                                ))}
                                                <li>
                                                    <a
                                                        className="border-top mt-2 pt-3"
                                                        href="#"
                                                        onClick={handleSignOut}
                                                    >
                                                        Sign out
                                                    </a>
                                                </li>
                                            </ul>
                                        </li>
                                    </ul>
                                </div>
                            </li>
                        )}
                    </ul>
                    <a
                        id="MobileAppDownload"
                        href={getHref("/mobile")}
                        target="_blank"
                        rel="noopener noreferrer"
                    >
                        <div className="pt-5 text-center pb-5 mb-5">
                            <div className="mb-5 pb-5">
                                <div
                                    className="font_weight--bold color_carbon font_size--medium mx-auto pt-4 mb-4"
                                    style={{ maxWidth: 175 }}
                                >
                                    Enhance your real estate experience with HAR App
                                </div>
                                <img
                                    loading="lazy"
                                    alt="Apple Store"
                                    className="pr-3 pe-3"
                                    src="https://content.harstatic.com/media/icons/icon-apple-carbon.svg"
                                />
                                <img
                                    loading="lazy"
                                    alt="Google Play"
                                    className="pl-3 ps-3"
                                    src="https://content.harstatic.com/media/icons/icon-android-carbon.svg"
                                />
                            </div>
                        </div>
                    </a>
                </div>
                {props.children}
            </div>
            {/* Backdrop for the mobile drawer. It stays collapsed until the
                `menu_opened` body class turns it into a full-screen overlay
                (see .menu_opened .menu_overlay in layout.css). */}
            <div className="menu_overlay" onClick={props.onCloseMenu} />
        </nav>
    );
};
